import isUrl from 'is-url';
// slate runtime primitives from the live-binding holder (see slate-runtime.ts).
import { Element as SlateElement, Editor, Node, Range, Transforms } from "@/extension/utils/slate-runtime";

const URL_REGEX = /(https?:\/\/\S+|www\.\S+)/gi;

const normalizeLinkUrl = (url: string) => {
  return url.startsWith("www.") ? `https://${url}` : url;
};

const getUrlMatches = (text: string) => {
  return [...text.matchAll(new RegExp(URL_REGEX.source, "gi"))];
};

const splitTextIntoInlineNodes = (text: string) => {
  const nodes: any[] = [];
  const matches = getUrlMatches(text);
  let lastIndex = 0;

  for (const match of matches) {
    const urlText = match[0];
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      nodes.push({ text: text.slice(lastIndex, matchIndex) });
    }

    nodes.push({
      type: "link",
      url: normalizeLinkUrl(urlText),
      children: [{ text: urlText }],
    });

    lastIndex = matchIndex + urlText.length;
  }

  if (lastIndex < text.length) {
    nodes.push({ text: text.slice(lastIndex) });
  }

  if (nodes.length === 0) {
    nodes.push({ text });
  }

  return { nodes, matches };
};

export const withLinks = (editor: Editor) => {
  const { insertData, insertText, isInline, normalizeNode } = editor;

  editor.isInline = (element: any) => {
    return element.type === "link" ? true : isInline(element);
  };

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (SlateElement.isElement(node) && (node as any).type === "link") {
      const linkText = Node.string(node);
      const trimmedText = linkText.trim();
      const { nodes: splitNodes, matches } = splitTextIntoInlineNodes(linkText);

      if (!trimmedText) {
        Transforms.unwrapNodes(editor, { at: path });
        return;
      }

      // Keep intentional custom labels such as "click here".
      if (matches.length === 0) {
        if ((node as any).isCustomText) {
          normalizeNode(entry);
          return;
        }

        if (!/\s/.test(trimmedText)) {
          Transforms.unwrapNodes(editor, { at: path });
          return;
        }

        normalizeNode(entry);
        return;
      }

      const firstMatch = matches[0];
      const isOnlyUrlText =
        matches.length === 1 &&
        (firstMatch.index ?? 0) === 0 &&
        firstMatch[0].length === linkText.length;

      if (!isOnlyUrlText) {
        Editor.withoutNormalizing(editor, () => {
          Transforms.removeNodes(editor, { at: path });
          Transforms.insertNodes(editor, splitNodes, { at: path });
        });
        return;
      }

      const normalizedUrl = normalizeLinkUrl(firstMatch[0]);
      if ((node as any).url !== normalizedUrl) {
        Transforms.setNodes(editor, { url: normalizedUrl }, { at: path });
        return;
      }
    }

    normalizeNode(entry);
  };

  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };

  editor.insertData = (data) => {
    const text = data.getData("text/plain");

    if (text) {
      if (getUrlMatches(text).length > 0) {
        // Split by newlines to handle multi-line pastes
        const lines = text.split('\n');
        const fragment: any[] = lines.map((line) => {
          const { nodes: children, matches } = splitTextIntoInlineNodes(line);

          if (matches.length === 0) {
            return { type: 'paragraph', children: [{ text: line }] };
          }

          return { type: 'paragraph', children };
        });

        Transforms.insertFragment(editor, fragment);
        return;
      }
    }

    insertData(data);
  };

  return editor;
};

export const insertLink = (editor: Editor, url: string) => {
  if (editor.selection) {
    wrapLink(editor, url);
  }
};

export const isLinkActive = (editor: Editor) => {
  const [link]: any = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
  });
  return !!link;
};

 export const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
  });
};

 const wrapLink = (editor: Editor, url: string) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: any = {
    type: "link",
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: "end" });
  }
};
